import React, { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Navigation } from '../navigation/Navigation';
import { useMediaQuery } from '@/hooks/use-media-query';
import TopBar from './TopBar';

interface AppLayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  transparentHeader?: boolean;
  className?: string;
  pageTitle?: string;
}

/**
 * AppLayout integrates both the new navigation system and maintains compatibility
 * with the existing header. It provides a responsive layout with:
 * - Desktop: Left sidebar navigation + optional top header
 * - Mobile: Bottom navigation bar + optional top header
 */
const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  showHeader = true,
  transparentHeader = true,
  className,
  pageTitle,
}) => {
  // Use media query to determine which layout to use
  const isDesktop = useMediaQuery('(min-width: 768px)');

  return (
    <div className="flex flex-col min-h-screen bg-[#1A1A1B] text-[#D7DADC]">
      {/* Optional header */}
      {showHeader && (
        <TopBar
          onTagSelect={() => {}}
        />
      )}

      <div className="flex flex-1 relative">
        {/* Desktop sidebar navigation - only shown on larger screens */}
        {isDesktop && (
          <div className="hidden md:block h-full sticky top-0">
            <Navigation />
          </div>
        )}

        {/* Main content */}
        <main 
          className={cn(
            "flex-1 min-h-0 w-full",
            // Add padding-bottom on mobile for bottom nav
            !isDesktop && "pb-16",
            // Add padding-top if header is shown
            showHeader && "pt-16",
            className
          )}
        >
          {children}
        </main>
      </div>

      {/* Mobile bottom navigation - only shown on smaller screens */}
      {!isDesktop && <Navigation />}
    </div>
  );
};

export default AppLayout;
